import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RevoltModal } from '../../../../../components/RevoltModal';

/**
 * Wrapper that enables pinch-to-zoom and pan for touch devices.
 * Uses CSS transform for scale and translate.
 */
function ZoomPanChart({ children, className = '' }) {
  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const lastRef = useRef({ touches: [], scale: 1, translate: { x: 0, y: 0 } });

  const stateRef = useRef({ scale: 1, translate: { x: 0, y: 0 } });
  stateRef.current = { scale, translate };

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const wheelHandler = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;
      const { scale: s, translate: t } = stateRef.current;
      const rect = container.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const delta = e.deltaY > 0 ? -0.15 : 0.15;
      const newScale = Math.min(4, Math.max(0.5, s + delta));
      const contentX = (centerX - t.x) / s;
      const contentY = (centerY - t.y) / s;
      setTranslate({
        x: centerX - contentX * newScale,
        y: centerY - contentY * newScale,
      });
      setScale(newScale);
    };
    el.addEventListener('wheel', wheelHandler, { passive: false });
    return () => el.removeEventListener('wheel', wheelHandler);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const touchStart = (e) => {
      if (e.touches.length === 0) return;
      lastRef.current = {
        touches: Array.from(e.touches).map((t) => ({ x: t.clientX, y: t.clientY })),
        scale: stateRef.current.scale,
        translate: { ...stateRef.current.translate },
      };
    };

    const touchMove = (e) => {
      e.preventDefault();
      if (e.touches.length === 0) return;
      const last = lastRef.current;
      const rect = el.getBoundingClientRect();
      let newScale = last.scale;
      let newTranslate = { ...last.translate };

      if (e.touches.length >= 2) {
        const t0 = e.touches[0];
        const t1 = e.touches[1];
        const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
        const centerX = (t0.clientX + t1.clientX) / 2 - rect.left;
        const centerY = (t0.clientY + t1.clientY) / 2 - rect.top;

        if (last.touches.length >= 2) {
          const lastDist = Math.hypot(last.touches[1].x - last.touches[0].x, last.touches[1].y - last.touches[0].y);
          const lastCenterX = (last.touches[0].x + last.touches[1].x) / 2 - rect.left;
          const lastCenterY = (last.touches[0].y + last.touches[1].y) / 2 - rect.top;
          if (lastDist > 0) {
            newScale = Math.min(4, Math.max(0.5, last.scale * (dist / lastDist)));
            const contentX = (lastCenterX - last.translate.x) / last.scale;
            const contentY = (lastCenterY - last.translate.y) / last.scale;
            newTranslate = {
              x: centerX - contentX * newScale,
              y: centerY - contentY * newScale,
            };
          }
        }
      } else if (e.touches.length === 1 && last.touches.length === 1) {
        const dx = e.touches[0].clientX - last.touches[0].x;
        const dy = e.touches[0].clientY - last.touches[0].y;
        newTranslate = { x: last.translate.x + dx, y: last.translate.y + dy };
      }

      setScale(newScale);
      setTranslate(newTranslate);
      lastRef.current = {
        touches: Array.from(e.touches).map((t) => ({ x: t.clientX, y: t.clientY })),
        scale: newScale,
        translate: newTranslate,
      };
    };

    const touchEnd = (e) => {
      if (e.touches.length > 0) {
        lastRef.current = {
          ...lastRef.current,
          touches: Array.from(e.touches).map((t) => ({ x: t.clientX, y: t.clientY })),
        };
      }
    };

    const opts = { passive: false };
    el.addEventListener('touchstart', touchStart);
    el.addEventListener('touchmove', touchMove, opts);
    el.addEventListener('touchend', touchEnd);
    el.addEventListener('touchcancel', touchEnd);

    return () => {
      el.removeEventListener('touchstart', touchStart);
      el.removeEventListener('touchmove', touchMove);
      el.removeEventListener('touchend', touchEnd);
      el.removeEventListener('touchcancel', touchEnd);
    };
  }, []);

  const handleDoubleTap = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const resetZoom = () => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  };

  return (
    <div
      className={`stripe-chart-zoom-container ${className}`.trim()}
      ref={containerRef}
    >
      <span className="stripe-chart-zoom-hint">Pinch to zoom, drag to pan · Double-tap to reset</span>
      <div
        ref={innerRef}
        className="stripe-chart-zoom-inner"
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transformOrigin: '0 0',
        }}
        onDoubleClick={handleDoubleTap}
      >
        {children}
      </div>
      {(scale !== 1 || translate.x !== 0 || translate.y !== 0) && (
        <button
          type="button"
          className="stripe-chart-zoom-reset"
          onClick={resetZoom}
          aria-label="Reset zoom"
        >
          Reset zoom
        </button>
      )}
    </div>
  );
}

/**
 * Format group key for display: "pro-basic" -> "Pro - Basic"
 */
function formatGroupLabel(key) {
  if (key.includes('-')) {
    const [plan, ...tierParts] = key.split('-');
    const tier = tierParts.join('-');
    return `${plan.charAt(0).toUpperCase() + plan.slice(1)} - ${tier.charAt(0).toUpperCase() + tier.slice(1)}`;
  }
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * SVG line chart for Stripe transaction amounts over time.
 * Supports "all" (single line), "byPlan" (by plan), or "byTier" (by plan+tier).
 */
export function StripeTransactionChart({ chartData, chartView, getPlanColor }) {
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const { paths, maxVal, dates, planIds } = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return { points: [], paths: [], maxVal: 0, minVal: 0, dates: [], planIds: [] };
    }

    const dates = chartData.map((d) => d.date);
    const allPlans = new Set();
    chartData.forEach((d) => Object.keys(d.byPlan || {}).forEach((p) => allPlans.add(p)));
    const planIds = Array.from(allPlans).sort();

    const allTotals = chartData.map((d) => d.total);
    const maxVal = Math.max(...allTotals, 1);
    const minVal = 0;

    const w = 800;
    const h = 300;
    const pad = { top: 20, right: 20, bottom: 40, left: 50 };
    const plotW = w - pad.left - pad.right;
    const plotH = h - pad.top - pad.bottom;

    const xScale = (i) => pad.left + (i / Math.max(1, dates.length - 1)) * plotW;
    const yScale = (v) => pad.top + plotH - (v / maxVal) * plotH;

    const points = chartData.map((d, i) => ({
      x: xScale(i),
      y: yScale(d.total),
      date: d.date,
      total: d.total,
    }));

    const paths = [];

    if (chartView === 'all') {
      const start = points[0];
      const rest = points.slice(1).map((p) => `L ${p.x} ${p.y}`).join(' ');
      const d = start ? `M ${start.x} ${start.y} ${rest}` : '';
      paths.push({ key: 'all', d, color: '#8b0000' });
    } else {
      for (const planId of planIds) {
        const pts = chartData.map((d, i) => {
          const v = (d.byPlan || {})[planId] || 0;
          return { x: xScale(i), y: yScale(v), v };
        });
        const d = pts.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ');
        paths.push({ key: planId, d, color: getPlanColor(planId) });
      }
    }

    return { points, paths, maxVal, minVal, dates, planIds };
  }, [chartData, chartView, getPlanColor]);

  if (chartData.length === 0) {
    return (
      <div className="stripe-chart-empty">
        <p>No transaction data to display.</p>
        <p className="stripe-chart-empty-hint">Complete some Stripe checkouts to see the chart.</p>
      </div>
    );
  }

  const w = 800;
  const h = 300;
  const pad = { top: 20, right: 20, bottom: 40, left: 50 };

  const chartContent = (
    <>
      <svg viewBox={`0 0 ${w} ${h}`} className="stripe-chart-svg" preserveAspectRatio="xMidYMid meet">
        {/* Grid lines */}
        {[0.25, 0.5, 0.75, 1].map((pct) => (
          <line
            key={pct}
            x1={pad.left}
            y1={pad.top + (1 - pct) * (h - pad.top - pad.bottom)}
            x2={w - pad.right}
            y2={pad.top + (1 - pct) * (h - pad.top - pad.bottom)}
            className="stripe-chart-grid"
          />
        ))}
        {/* Y-axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct) => (
          <text
            key={pct}
            x={pad.left - 8}
            y={pad.top + (1 - pct) * (h - pad.top - pad.bottom) + 4}
            className="stripe-chart-axis-label"
            textAnchor="end"
          >
            ${(maxVal * pct).toFixed(0)}
          </text>
        ))}
        {/* X-axis labels (show first, middle, last) */}
        {dates.length > 0 && (
          <>
            <text x={pad.left} y={h - 8} className="stripe-chart-axis-label" textAnchor="start">
              {dates[0]}
            </text>
            <text x={w / 2} y={h - 8} className="stripe-chart-axis-label" textAnchor="middle">
              {dates[Math.floor(dates.length / 2)]}
            </text>
            <text x={w - pad.right} y={h - 8} className="stripe-chart-axis-label" textAnchor="end">
              {dates[dates.length - 1]}
            </text>
          </>
        )}
        {/* Line paths */}
        {paths.map(({ key, d, color }) => (
          <path
            key={key}
            d={d}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="stripe-chart-line"
          />
        ))}
      </svg>
      {/* Legend for byPlan and byTier */}
      {(chartView === 'byPlan' || chartView === 'byTier') && planIds.length > 0 && (
        <div className="stripe-chart-legend">
          {planIds.map((planId) => (
            <span key={planId} className="stripe-chart-legend-item">
              <span
                className="stripe-chart-legend-dot"
                style={{ backgroundColor: getPlanColor(planId) }}
              />
              {formatGroupLabel(planId)}
            </span>
          ))}
        </div>
      )}
    </>
  );

  return (
    <>
      <div
        className="stripe-chart-wrapper stripe-chart-clickable"
        onClick={() => setChartModalOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setChartModalOpen(true)}
        aria-label="View chart larger"
      >
        {chartContent}
        <span className="stripe-chart-tap-hint">Tap to view larger</span>
      </div>
      <RevoltModal
        isOpen={chartModalOpen}
        onClose={() => setChartModalOpen(false)}
        title="Transaction Chart"
        containerClassName="stripe-chart-modal"
      >
        <ZoomPanChart className="stripe-chart-modal-content">
          {chartContent}
        </ZoomPanChart>
      </RevoltModal>
    </>
  );
}
